import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import './Registration.scss';

interface IndividualRegistrationProps {
  event: any;
  category: any;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
}

const IndividualRegistration: React.FC<IndividualRegistrationProps> = ({ 
  event, 
  category, 
  onSuccess, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'BD',
    notes: '',
    document: null as File | null
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, document: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'document' && value) {
          formDataToSend.append('document', value);
        } else if (key !== 'document') {
          formDataToSend.append(key, value as string);
        }
      });

      formDataToSend.append('event_slug', event.slug);
      formDataToSend.append('category_id', category.id.toString());

      const response = await apiService.registration.individual(formDataToSend);

      if (response.success) {
        setSuccess(true);
        
        // Dispatch cart update event to notify header
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { message: 'Cart updated successfully' }
        }));
        
        onSuccess?.(response);
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="registration-success">
        <Card className="text-center">
          <Card.Body>
            <div className="success-icon mb-3">
              <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
            </div>
            <h3 className="text-success mb-3">Registration Successful!</h3>
            <p className="text-muted mb-4">
              You have successfully registered for <strong>{category.name}</strong>.
            </p>
            <p className="text-muted mb-4">
              A confirmation email has been sent to <strong>{formData.email}</strong>.
            </p>
            <Button variant="primary" onClick={onCancel}>
              Close
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="individual-registration">
      <Card>
        <Card.Header>
          <h4 className="mb-0">Individual Registration</h4>
          <p className="text-muted mb-0">Register for {category.name}</p>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required">First Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your first name"
                    className="simple-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required">Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your last name"
                    className="simple-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required">Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email address"
                    className="simple-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <InternationalPhoneInput
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter your phone number"
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    showLabel={true}
                    required={true}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter your address"
                className="simple-input"
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter your city"
                    className="simple-input"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    placeholder="Enter postal code"
                    className="simple-input"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="simple-input"
                  >
                    <option value="BD">Bangladesh</option>
                    <option value="US">United States</option>
                    <option value="UK">United Kingdom</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="IN">India</option>
                    <option value="TH">Thailand</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="required">Document Upload</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={handleFileChange}
                required
              />
              <Form.Text className="text-muted">
                Please upload a valid document (PDF, JPG, PNG, DOC, DOCX)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Additional Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Any additional information or special requirements"
                className="simple-input"
              />
            </Form.Group>

            <div className="registration-actions">
              <Button
                type="button"
                variant="link"
                onClick={onCancel}
                className="btn-cancel"
              >
                <i className="fas fa-times"></i>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Registering...
                  </>
                ) : (
                  'Register Now'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default IndividualRegistration;
